import { useEffect, useState } from "react";
import { X, Printer, Receipt, FileText, ClipboardList, Car, ShieldCheck, ArrowRightLeft, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import { StatusBadge, fmtDate, fmtCurrency } from "../../lib/status";
import {
  getConfirmationTokenRecord, getPortalBookingRecord,
  getConfirmationTokenRecordPhoto, getPortalBookingRecordPhoto,
} from "../../lib/conditionRecord";
import ConfirmationHistoryPanel from "../ConfirmationHistoryPanel";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

const DEPOSIT_STATUS_LABEL = {
  not_required: "Not required", pending: "Pending", received: "Received",
  refund_pending: "Refund pending", refunded: "Refunded",
  partially_withheld: "Partially withheld", fully_withheld: "Withheld",
};

/**
 * Booking & Condition Record V1: a durable "rental receipt + condition
 * record" for one already-authorized booking. Reuses existing systems
 * end-to-end (confirmation-version history via ConfirmationHistoryPanel,
 * deposit lifecycle labels, inspection/mileage data) rather than a new
 * data model. This is a record/receipt, not a dashboard: sections with
 * no real data are simply omitted.
 */
export default function BookingConditionRecordModal({ source, onClose }) {
  const { panelRef } = useAccessibleOverlay(true, onClose);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetcher = source.mode === "token"
      ? getConfirmationTokenRecord(source.token)
      : getPortalBookingRecord(source.bookingId);
    fetcher
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (cancelled) return;
        const msg = formatApiError(e);
        setError(msg);
        toast.error(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.mode, source.token, source.bookingId]);

  const b = data?.booking;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto print:hidden print:static print:bg-white print:backdrop-blur-none"
         data-testid="booking-condition-record-modal">
      <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6 print:p-0 print:block print:min-h-0">
        <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="bcr-title"
             className="bg-white rounded-2xl border border-slate-200 max-w-3xl w-full overflow-hidden print:rounded-none print:border-0 print:max-w-none">
          <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200 print:hidden">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1">
                <Receipt className="w-3.5 h-3.5" /> Booking &amp; Condition Record
              </div>
              <h2 id="bcr-title" className="font-display text-2xl font-semibold mt-0.5" data-testid="bcr-title">
                {b?.booking_reference || (loading ? "Loading…" : "Record")}
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {b && (
                <button type="button" onClick={() => window.print()} data-testid="bcr-print"
                        className="text-xs font-medium px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 inline-flex items-center gap-1.5">
                  <Printer className="w-3.5 h-3.5" /> Print record
                </button>
              )}
              <button type="button" onClick={onClose} aria-label="Close" data-testid="bcr-close"
                      className="p-1.5 rounded-md hover:bg-slate-100">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="p-5 space-y-6 max-h-[80vh] overflow-y-auto print:max-h-none print:overflow-visible print:p-6">
            {loading && <div className="text-sm text-slate-500 py-6 text-center">Loading your record…</div>}
            {error && !loading && (
              <div className="text-sm text-rose-600 py-6 text-center" data-testid="bcr-error">
                Couldn't load your record: {error}
              </div>
            )}

            {b && (
              <>
                <div className="hidden print:block mb-2">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Booking &amp; Condition Record</div>
                  <h1 className="font-display text-2xl font-bold">{b.booking_reference}</h1>
                </div>

                {/* 1. Booking summary */}
                <Section title="Booking summary" testId="bcr-summary" icon={<Receipt className="w-3.5 h-3.5" />}>
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <StatusBadge status={b.status} isHold={b.is_hold} changeRequested={b.change_requested}
                                 cancelledBy={b.cancelled_by} cancellationReason={b.cancellation_reason}
                                 reconfirmationRequired={b.reconfirmation_required} />
                    {data.company?.name && (
                      <span className="text-sm text-slate-600">{data.company.name}{data.company.city ? ` · ${data.company.city}` : ""}</span>
                    )}
                  </div>
                  <dl className="grid sm:grid-cols-2 gap-3 text-sm">
                    <Def label="Vehicle" value={data.vehicle ? `${data.vehicle.name}${data.vehicle.plate_number ? ` · ${data.vehicle.plate_number}` : ""}` : "-"} />
                    <Def label="Pickup" value={fmtDate(b.start_datetime)} sub={b.pickup_location} />
                    <Def label="Return" value={fmtDate(b.end_datetime)} sub={b.return_location} />
                    <Def label="Agreed rental price" value={fmtCurrency(b.price)} />
                    {b.rental_service_type === "with_driver" && (
                      <Def label="Driver service" value={b.driver_display_name || "Assigned"} sub={fmtCurrency(b.driver_service_price)} />
                    )}
                    {b.customer_visible_notes && <Def label="Notes" value={b.customer_visible_notes} />}
                  </dl>
                  <p className="text-[11px] text-slate-400 mt-3">
                    Agreed rental price reflects the rate agreed for this booking: ConfirmPH records booking
                    and deposit status but does not process or hold rental payments.
                  </p>
                </Section>

                {/* 2. What was agreed */}
                {data.agreed_terms?.length > 0 && (
                  <div className="break-inside-avoid">
                    <ConfirmationHistoryPanel bookingId={b.id} versions={data.agreed_terms} />
                  </div>
                )}

                {/* 3. Deposit */}
                {data.deposit && data.deposit.status !== "not_required" && (
                  <Section title="Deposit" testId="bcr-deposit" icon={<ShieldCheck className="w-3.5 h-3.5" />}>
                    <dl className="grid sm:grid-cols-2 gap-3 text-sm">
                      <Def label="Status" value={DEPOSIT_STATUS_LABEL[data.deposit.status] || data.deposit.status} />
                      <Def label="Required" value={fmtCurrency(data.deposit.required)} />
                      <Def label="Collected" value={fmtCurrency(data.deposit.collected)} />
                      {data.deposit.outstanding_to_collect > 0 && <Def label="Outstanding" value={fmtCurrency(data.deposit.outstanding_to_collect)} />}
                      {data.deposit.refunded > 0 && <Def label="Refunded" value={fmtCurrency(data.deposit.refunded)} />}
                      {data.deposit.withheld > 0 && <Def label="Withheld" value={fmtCurrency(data.deposit.withheld)} />}
                      {data.deposit.withheld > 0 && data.deposit.withheld_reason && <Def label="Reason" value={data.deposit.withheld_reason} />}
                    </dl>
                  </Section>
                )}

                {/* 4/5. Pickup + return condition */}
                {data.pickup && (
                  <ConditionSection title="Pickup condition" testId="bcr-pickup" source={source}
                                     inspection={data.pickup.inspection} handover={data.pickup.handover}
                                     photos={data.pickup.photos} />
                )}
                {data.return && (
                  data.return.pending ? (
                    <Section title="Return condition" testId="bcr-return-pending" icon={<ArrowRightLeft className="w-3.5 h-3.5" />}>
                      <p className="text-sm text-slate-500">Return inspection not recorded yet.</p>
                    </Section>
                  ) : (
                    <ConditionSection title="Return condition" testId="bcr-return"
                                       source={source} inspection={data.return.inspection} photos={data.return.photos} />
                  )
                )}

                {/* 6. Comparison */}
                {data.comparison && (
                  <Section title="At pickup vs at return" testId="bcr-comparison" icon={<ArrowRightLeft className="w-3.5 h-3.5" />}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                            <th className="py-1 pr-3"></th>
                            <th className="py-1 pr-3">At pickup</th>
                            <th className="py-1">At return</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          <tr>
                            <td className="py-1.5 pr-3 text-slate-500">Odometer</td>
                            <td className="py-1.5 pr-3">{data.comparison.pickup_odometer_km != null ? `${data.comparison.pickup_odometer_km.toLocaleString()} km` : "-"}</td>
                            <td className="py-1.5">{data.comparison.return_odometer_km != null ? `${data.comparison.return_odometer_km.toLocaleString()} km` : "-"}</td>
                          </tr>
                          <tr>
                            <td className="py-1.5 pr-3 text-slate-500">Fuel level</td>
                            <td className="py-1.5 pr-3">{data.comparison.pickup_fuel_level != null ? `${data.comparison.pickup_fuel_level}%` : "-"}</td>
                            <td className="py-1.5">{data.comparison.return_fuel_level != null ? `${data.comparison.return_fuel_level}%` : "-"}</td>
                          </tr>
                          <tr>
                            <td className="py-1.5 pr-3 text-slate-500">Inspection status</td>
                            <td className="py-1.5 pr-3 capitalize">{data.comparison.pickup_inspection_status || "-"}</td>
                            <td className="py-1.5 capitalize">{data.comparison.return_inspection_status || "-"}</td>
                          </tr>
                        </tbody>
                      </table>
                      {data.comparison.distance_driven_km != null && (
                        <p className="text-sm text-slate-700 mt-2">
                          Distance driven: <b>{data.comparison.distance_driven_km.toLocaleString()} km</b>
                        </p>
                      )}
                    </div>
                  </Section>
                )}

                {/* Existing damage on record */}
                {data.damages?.length > 0 && (
                  <Section title="Existing vehicle damage on record" testId="bcr-damages" icon={<ClipboardList className="w-3.5 h-3.5" />}>
                    <ul className="divide-y divide-slate-100 -mx-2">
                      {data.damages.map((d) => (
                        <li key={d.id} className="px-2 py-1.5 text-sm flex items-center gap-2 flex-wrap" data-testid={`bcr-damage-${d.id}`}>
                          <span className="font-medium capitalize">{(d.tag_label || d.tag_code || "").replace(/_/g, " ")}</span>
                          <span className="text-xs text-slate-500 uppercase">{d.severity}</span>
                          <span className="text-xs text-slate-500">· {d.view}</span>
                        </li>
                      ))}
                    </ul>
                    {data.damage_acknowledgement?.acknowledged_at && (
                      <p className="text-xs text-emerald-700 mt-2">
                        Acknowledged by {data.damage_acknowledgement.acknowledged_by} on {fmtDate(data.damage_acknowledgement.acknowledged_at)}.
                      </p>
                    )}
                  </Section>
                )}

                {/* 7. Documents */}
                {data.documents?.length > 0 && (
                  <Section title="Documents" testId="bcr-documents" icon={<FileText className="w-3.5 h-3.5" />}>
                    <ul className="divide-y divide-slate-100 -mx-2">
                      {data.documents.map((doc) => (
                        <li key={doc.id} className="px-2 py-2 text-sm flex items-center justify-between gap-2 flex-wrap" data-testid={`bcr-document-${doc.id}`}>
                          <div>
                            <div className="font-medium">{doc.title}</div>
                            <div className="text-xs text-slate-500 capitalize">{(doc.document_type || "").replace(/_/g, " ")}</div>
                          </div>
                          {doc.file_url && (
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-700 hover:underline print:hidden">
                              View document →
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                {/* 8. History */}
                {data.timeline?.length > 0 && (
                  <Section title="Booking history" testId="bcr-timeline" icon={<ClipboardList className="w-3.5 h-3.5" />}>
                    <ol className="relative border-l border-slate-200 ml-2 pl-4 space-y-2">
                      {data.timeline.map((e) => (
                        <li key={e.id} className="text-sm relative" data-testid={`bcr-timeline-${e.id}`}>
                          <span className="absolute -left-[22px] top-1.5 w-2 h-2 rounded-full bg-[#1B4332]" />
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <span className="font-medium text-slate-900">{e.label}</span>
                            <span className="text-xs text-slate-500">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</span>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </Section>
                )}
              </>
            )}
          </div>

          <div className="border-t border-slate-200 p-4 flex justify-end print:hidden">
            <button type="button" onClick={onClose} data-testid="bcr-back"
                    className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium">
              ← Back to my booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConditionSection({ title, testId, source, inspection, handover, photos }) {
  if (!inspection && !handover) return null;
  return (
    <Section title={title} testId={testId} icon={<Car className="w-3.5 h-3.5" />}>
      <dl className="grid sm:grid-cols-2 gap-3 text-sm">
        {inspection?.odometer_km != null && <Def label="Odometer" value={`${inspection.odometer_km.toLocaleString()} km`} />}
        {inspection?.fuel_level != null && <Def label="Fuel" value={`${inspection.fuel_level}%`} />}
        {inspection?.cleaning_condition && <Def label="Cleaning condition" value={inspection.cleaning_condition.replace(/_/g, " ")} />}
        {inspection && <Def label="Inspection status" value={<span className="capitalize">{inspection.status}</span>} />}
        {inspection?.finalized_at && <Def label="Finalized" value={fmtDate(inspection.finalized_at)} />}
        {handover?.actual_pickup_datetime && <Def label="Pickup time" value={fmtDate(handover.actual_pickup_datetime)} />}
        {inspection?.customer_acknowledged && (
          <Def label="Customer acknowledgement" value={inspection.customer_signature_name ? `Signed by ${inspection.customer_signature_name}` : "Acknowledged"} />
        )}
      </dl>
      {handover?.keys_items_released?.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Keys / items released</div>
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {handover.keys_items_released.map((it, i) => (
              <li key={i} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">
                {it.item}{it.released ? "" : " (not released)"}
              </li>
            ))}
          </ul>
        </div>
      )}
      {inspection?.checklist?.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Checklist</div>
          <ul className="mt-1 divide-y divide-slate-100 -mx-2">
            {inspection.checklist.map((c, i) => (
              <li key={i} className="px-2 py-1 text-sm flex items-center justify-between">
                <span>{c.label}</span>
                <span className="text-xs capitalize text-slate-500">{(c.rating || "").replace(/_/g, " ")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {photos?.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 inline-flex items-center gap-1">
            <ImageIcon className="w-3 h-3" /> Photos
          </div>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {photos.map((p) => <RecordPhotoThumb key={p.id} photo={p} source={source} />)}
          </div>
        </div>
      )}
    </Section>
  );
}

function RecordPhotoThumb({ photo, source }) {
  const [dataUrl, setDataUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    const fetcher = source.mode === "token"
      ? getConfirmationTokenRecordPhoto(source.token, photo.id)
      : getPortalBookingRecordPhoto(source.bookingId, photo.id);
    fetcher.then((d) => { if (alive) setDataUrl(d.data_url); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo.id, source.mode, source.token, source.bookingId]);
  if (!dataUrl) return <div className="w-16 h-16 rounded-lg bg-black/10 animate-pulse" />;
  return (
    <a href={dataUrl} target="_blank" rel="noreferrer" data-testid={`bcr-photo-${photo.id}`} className="print:hidden">
      <img src={dataUrl} alt={photo.caption || "Condition photo"} className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
    </a>
  );
}

function Section({ title, testId, icon, children }) {
  return (
    <section data-testid={testId} className="break-inside-avoid rounded-xl border border-slate-200 p-4 print:border-slate-300">
      <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1 mb-2">
        {icon} {title}
      </h3>
      {children}
    </section>
  );
}

function Def({ label, value, sub }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</dt>
      <dd className="text-slate-800 mt-0.5">{value}</dd>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}
